import { useEffect, useMemo, useState } from 'react';
import { Bot, Copy, KeyRound, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import { api, LoiMayChu } from '../services/api';
import type { McpAudit, McpScope, McpSession, TaiKhoanFB, TrangThaiExtension } from '../services/kieu';
import { AnhDaiDien, ChuaCoDuLieu, DangTai, DauThe, NhanTrangThai, NutChinh, NutPhu, The } from '../components/ui';
import { tenActor } from '../services/taiKhoan';

const TTL_OPTIONS = [
  { label: '1 giờ', value: 60 * 60 * 1000 },
  { label: '7 ngày', value: 7 * 24 * 60 * 60 * 1000 },
  { label: '30 ngày', value: 30 * 24 * 60 * 60 * 1000 },
];

const SCOPE_LABEL: Record<McpScope, string> = {
  'account:read': 'Đọc actor hiện tại',
  'groups:read': 'Đọc danh sách nhóm',
  'jobs:read': 'Đọc tiến trình/job',
  'jobs:write': 'Hủy job (không tạo bài/bình luận)',
  'posts:propose': 'Đề xuất bài đăng chờ duyệt',
  'comments:propose': 'Đề xuất bình luận chờ duyệt',
  'collections:read': 'Đọc bộ sưu tập nhóm',
  'campaigns:read': 'Đọc chiến dịch',
};

export function ManMcpAgent({ ext }: { ext: TrangThaiExtension | null }) {
  const [sessions, setSessions] = useState<McpSession[]>([]);
  const [audits, setAudits] = useState<McpAudit[]>([]);
  const [allowedScopes, setAllowedScopes] = useState<McpScope[]>(['account:read', 'groups:read', 'jobs:read', 'collections:read', 'campaigns:read']);
  const [dangTai, setDangTai] = useState(true);
  const [dangTao, setDangTao] = useState(false);
  const [loi, setLoi] = useState<string | null>(null);
  const [tokenMoi, setTokenMoi] = useState<string | null>(null);
  const [daCopy, setDaCopy] = useState(false);

  const accounts = ext?.accounts || [];
  const [clientName, setClientName] = useState('Claude Desktop');
  const [clientType, setClientType] = useState('claude');
  const [actorUid, setActorUid] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [ttlMs, setTtlMs] = useState(TTL_OPTIONS[1].value);
  const [scopes, setScopes] = useState<McpScope[]>(['account:read', 'groups:read', 'jobs:read', 'collections:read', 'campaigns:read']);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (actorUid || accounts.length === 0) return;
    const first = accounts[0];
    setActorUid(first.uid);
    setDeviceId(first.deviceId || '');
  }, [accounts, actorUid]);

  const actor = useMemo(() => accounts.find(a => a.uid === actorUid) || null, [accounts, actorUid]);
  const devices = useMemo(() => [...new Set(accounts.map(a => a.deviceId).filter(Boolean))], [accounts]);
  const readScopes = useMemo(() => allowedScopes.filter(scope => scope.endsWith(':read')), [allowedScopes]);
  const writeScopes = useMemo(() => allowedScopes.filter(scope => !scope.endsWith(':read')), [allowedScopes]);

  async function taiLai() {
    try {
      setDangTai(true);
      const [dsSession, dsAudit] = await Promise.all([api.mcpSessions(), api.mcpAudits(100)]);
      setSessions(dsSession.sessions || []);
      setAudits(dsAudit.audits || []);
      if (dsSession.allowedScopes?.length) setAllowedScopes(dsSession.allowedScopes);
      setLoi(null);
    } catch (err) {
      setLoi(err instanceof LoiMayChu ? err.message : 'Không tải được danh sách MCP token.');
    } finally {
      setDangTai(false);
    }
  }

  useEffect(() => { taiLai(); }, []);

  function toggleScope(scope: McpScope) {
    setScopes(cur => cur.includes(scope) ? cur.filter(s => s !== scope) : [...cur, scope]);
  }

  async function taoToken() {
    if (!actorUid) return setLoi('Phải chọn actor Facebook/Page trước khi sinh khóa.');
    if (scopes.length === 0) return setLoi('Phải chọn ít nhất một quyền MCP.');
    try {
      setDangTao(true);
      setDaCopy(false);
      const res = await api.taoMcpSession({
        clientName: clientName.trim(),
        clientType,
        deviceId: deviceId || actor?.deviceId || null,
        actorUid,
        actorKind: actor?.isPage ? 'Page' : 'User',
        scopes,
        ttlMs,
        note,
      });
      setTokenMoi(res.token);
      setSessions(cur => [res.session, ...cur.filter(s => s.id !== res.session.id)]);
      setLoi(null);
    } catch (err) {
      setLoi(err instanceof LoiMayChu ? err.message : 'Không tạo được MCP token.');
    } finally {
      setDangTao(false);
    }
  }

  async function huy(session: McpSession) {
    if (!confirm(`Thu hồi token ${session.clientName}? AI đang dùng khóa này sẽ mất quyền ngay.`)) return;
    try {
      await api.huyMcpSession(session.id);
      await taiLai();
    } catch (err) {
      setLoi(err instanceof LoiMayChu ? err.message : 'Không thu hồi được token.');
    }
  }

  async function copyToken() {
    if (!tokenMoi) return;
    await navigator.clipboard.writeText(tokenMoi);
    setDaCopy(true);
  }

  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in space-y-5">
      <div className="grid gap-5 xl:grid-cols-[minmax(0,420px)_1fr]">
        <The className="overflow-hidden">
          <DauThe icon={<KeyRound className="h-4 w-4" />} tieuDe="Sinh khóa MCP" />
          <div className="space-y-4 p-5">
            <div className="rounded-2xl bg-slate-950 p-4 text-white shadow-inner dark:bg-black">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                  <Bot className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-bold">AI chỉ được dùng đúng phạm vi bạn cấp</p>
                  <p className="text-xs text-slate-400">Token trói cứng tenant, thiết bị, actor và scope.</p>
                </div>
              </div>
            </div>

            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Tên client</label>
            <input value={clientName} onChange={e => setClientName(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Claude Desktop" />

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Loại</label>
                <select value={clientType} onChange={e => setClientType(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white">
                  <option value="claude">Claude</option>
                  <option value="cursor">Cursor</option>
                  <option value="postman">Postman</option>
                  <option value="mcp">MCP khác</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Thời hạn</label>
                <select value={ttlMs} onChange={e => setTtlMs(Number(e.target.value))} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white">
                  {TTL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Thiết bị Chrome</label>
              <select value={deviceId} onChange={e => setDeviceId(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white">
                <option value="">Theo actor đang chọn</option>
                {devices.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Actor Facebook/Page</label>
              {accounts.length === 0 ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                  Chưa có Extension/actor online. Kết nối Chrome trước khi sinh khóa.
                </div>
              ) : (
                <div className="space-y-2">
                  {accounts.map(a => (
                    <button key={a.uid} type="button" onClick={() => { setActorUid(a.uid); setDeviceId(a.deviceId || ''); }} className={'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ' + (actorUid === a.uid ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10' : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/50')}>
                      <AnhDaiDien src={a.avatar} ten={tenActor(a)} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-bold text-slate-900 dark:text-white">{tenActor(a)}</span>
                        <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{a.isPage ? 'Page' : 'Cá nhân'} · {a.uid} · {a.deviceId || 'không rõ thiết bị'}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Quyền MCP</label>
              <div className="space-y-3">
                <div>
                  <p className="mb-2 text-xs font-semibold text-slate-500 dark:text-slate-400">Quyền đọc</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {readScopes.map(scope => (
                      <label key={scope} className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700 dark:text-slate-200">
                        <input type="checkbox" checked={scopes.includes(scope)} onChange={() => toggleScope(scope)} className="h-4 w-4 rounded border-slate-300 text-brand-600" />
                        {SCOPE_LABEL[scope] || scope}
                      </label>
                    ))}
                  </div>
                </div>
                {writeScopes.length > 0 && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
                    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">Quyền ghi có rủi ro</p>
                    <div className="grid gap-2">
                      {writeScopes.map(scope => (
                        <label key={scope} className="flex cursor-pointer items-center gap-2 rounded-xl border border-amber-200 bg-white p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-slate-950 dark:text-amber-200">
                          <input type="checkbox" checked={scopes.includes(scope)} onChange={() => toggleScope(scope)} className="h-4 w-4 rounded border-amber-300 text-amber-600" />
                          {SCOPE_LABEL[scope] || scope}
                        </label>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">Quyền ghi chỉ được hủy job hoặc tạo đề xuất chờ người dùng duyệt. AI vẫn chưa được tự đăng bài hoặc bình luận.</p>
                  </div>
                )}
              </div>
            </div>

            <textarea value={note} onChange={e => setNote(e.target.value)} className="h-20 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Ghi chú nội bộ, ví dụ: Token Claude máy công ty" />

            {loi && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-400">{loi}</div>}

            <NutChinh onClick={taoToken} disabled={dangTao || !actorUid || scopes.length === 0}>
              <KeyRound className="h-4 w-4" />
              {dangTao ? 'Đang sinh khóa...' : 'Sinh khóa'}
            </NutChinh>
          </div>
        </The>

        <div className="space-y-5">
          {tokenMoi && (
            <The className="overflow-hidden border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10">
              <DauThe icon={<ShieldCheck className="h-4 w-4" />} tieuDe="Token mới - chỉ hiện một lần" />
              <div className="space-y-3 p-5">
                <p className="text-sm text-emerald-800 dark:text-emerald-300">Hãy dán token này vào Claude/Cursor/Postman ngay. Sau khi rời màn hình, hệ thống không thể hiện lại token.</p>
                <div className="break-all rounded-xl bg-white p-3 font-mono text-xs text-slate-800 shadow-sm dark:bg-slate-950 dark:text-slate-200">{tokenMoi}</div>
                <div className="flex flex-wrap gap-2">
                  <NutChinh onClick={copyToken}><Copy className="h-4 w-4" />{daCopy ? 'Đã copy' : 'Copy token'}</NutChinh>
                  <NutPhu onClick={() => setTokenMoi(null)}>Ẩn token</NutPhu>
                </div>
              </div>
            </The>
          )}

          <The className="overflow-hidden">
            <DauThe icon={<Bot className="h-4 w-4" />} tieuDe="Token đã cấp" dem={sessions.length} phai={<NutPhu onClick={taiLai}><RefreshCw className="h-3.5 w-3.5" />Tải lại</NutPhu>} />
            {dangTai ? <DangTai dong={4} /> : sessions.length === 0 ? (
              <ChuaCoDuLieu chu="Chưa có token MCP nào." goiY="Sinh khóa đầu tiên để Claude/Cursor đọc dữ liệu Lab Server." />
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
                {sessions.map(s => <SessionRow key={s.id} session={s} accounts={accounts} onRevoke={() => huy(s)} />)}
              </div>
            )}
          </The>

          <The className="overflow-hidden">
            <DauThe icon={<ShieldCheck className="h-4 w-4" />} tieuDe="Nhật ký AI" dem={audits.length} phai={<NutPhu onClick={taiLai}><RefreshCw className="h-3.5 w-3.5" />Tải lại</NutPhu>} />
            {dangTai ? <DangTai dong={4} /> : audits.length === 0 ? (
              <ChuaCoDuLieu chu="Chưa có lời gọi MCP nào." goiY="Khi Claude/Cursor gọi tool, nhật ký sẽ hiện ở đây." />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700/60">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40 dark:text-slate-400">
                    <tr>
                      <th className="px-5 py-3 text-left font-bold">Thời gian</th>
                      <th className="px-5 py-3 text-left font-bold">Client</th>
                      <th className="px-5 py-3 text-left font-bold">Tool</th>
                      <th className="px-5 py-3 text-left font-bold">Actor</th>
                      <th className="px-5 py-3 text-left font-bold">Kết quả</th>
                      <th className="px-5 py-3 text-left font-bold">Thời lượng</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                    {audits.map(a => <AuditRow key={a.id} audit={a} accounts={accounts} />)}
                  </tbody>
                </table>
              </div>
            )}
          </The>
        </div>
      </div>
    </div>
  );
}

function AuditRow({ audit, accounts }: { audit: McpAudit; accounts: TaiKhoanFB[] }) {
  const actor = accounts.find(a => a.uid === audit.actorUid) || null;
  const keys = Array.isArray(audit.inputSummary?.keys) ? audit.inputSummary.keys.join(', ') : '';
  const summary = audit.inputSummary || {};
  const details = [
    typeof summary.jobId === 'string' ? `Job: ${summary.jobId}` : '',
    typeof summary.idempotencyKeyPrefix === 'string' ? `Idem: ${summary.idempotencyKeyPrefix}...` : '',
    typeof summary.reasonLength === 'number' ? `Lý do: ${summary.reasonLength} ký tự` : '',
    typeof summary.replayed === 'boolean' ? (summary.replayed ? 'Replay: có' : 'Replay: không') : '',
  ].filter(Boolean);
  const clientName = audit.session?.clientName || '[Đã thu hồi / Không rõ]';
  const ok = audit.resultStatus === 'OK';
  return (
    <tr className="align-top text-slate-700 dark:text-slate-200">
      <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-500 dark:text-slate-400">{new Date(audit.createdAt).toLocaleString('vi-VN')}</td>
      <td className="px-5 py-4">
        <p className="font-semibold">{clientName}</p>
        <p className="mt-1 text-xs text-slate-400">{audit.session?.tokenPrefix ? `Prefix: ${audit.session.tokenPrefix}` : 'Session không còn liên kết'}</p>
      </td>
      <td className="px-5 py-4">
        <p className="font-mono text-xs font-semibold">{audit.toolName}</p>
        <p className="mt-1 text-xs text-slate-400">Input: {keys || 'không có key'}</p>
        {details.length > 0 && <p className="mt-1 max-w-xs text-xs text-slate-500 dark:text-slate-400">{details.join(' · ')}</p>}
      </td>
      <td className="px-5 py-4">
        <p className="font-semibold">{actor ? tenActor(actor) : audit.actorUid || 'không rõ'}</p>
        <p className="mt-1 text-xs text-slate-400">{audit.actorUid || '-'}</p>
      </td>
      <td className="px-5 py-4">
        <NhanTrangThai chu={ok ? 'OK' : 'ERROR'} mau={ok ? 'xanh' : 'do'} />
        {audit.resultCode && <p className="mt-1 text-xs text-rose-500 dark:text-rose-400">{audit.resultCode}</p>}
        {audit.requestId && <p className="mt-1 text-xs text-slate-400">Req: {audit.requestId}</p>}
      </td>
      <td className="whitespace-nowrap px-5 py-4 text-xs text-slate-500 dark:text-slate-400">{audit.durationMs === null ? '-' : `${audit.durationMs}ms`}</td>
    </tr>
  );
}

function SessionRow({ session, accounts, onRevoke }: { session: McpSession; accounts: TaiKhoanFB[]; onRevoke: () => void }) {
  const actor = accounts.find(a => a.uid === session.actorUid) || null;
  const active = session.status === 'ACTIVE' && Date.parse(session.expiresAt) > Date.now();
  return (
    <div className="flex flex-col gap-3 p-5 lg:flex-row lg:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <AnhDaiDien src={actor?.avatar || null} ten={actor ? tenActor(actor) : session.actorUid || '?'} />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-bold text-slate-900 dark:text-white">{session.clientName}</p>
            <NhanTrangThai chu={active ? 'Đang hoạt động' : session.status === 'REVOKED' ? 'Đã thu hồi' : 'Hết hạn'} mau={active ? 'xanh' : 'xam'} />
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Actor: {actor ? tenActor(actor) : session.actorUid || 'không rõ'} · Thiết bị: {session.deviceId || 'không rõ'} · Prefix: {session.tokenPrefix}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Quyền: {session.scopes.join(', ')} · Hết hạn: {new Date(session.expiresAt).toLocaleString('vi-VN')} · Dùng cuối: {session.lastUsedAt ? new Date(session.lastUsedAt).toLocaleString('vi-VN') : 'chưa dùng'}
          </p>
        </div>
      </div>
      <button onClick={onRevoke} disabled={!active} className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-rose-500/30 dark:text-rose-400 dark:hover:bg-rose-500/10">
        <Trash2 className="h-3.5 w-3.5" />
        Thu hồi
      </button>
    </div>
  );
}
